import { Routes } from '@angular/router';
import { SITE_LINK, BreadcrumbConstant } from '@core/constants';

// Guards
import { AuthGuard } from '@core/guards/auth/auth.guard';
import { AppGuard } from '@core/guards/app/app-guard';
import { LoadGuard } from '@core/guards/load/load-guard';
import { PrivilegeGuard } from '@core/guards/privilege/privilege-guard';

// Layout components
import { AvantorSplashScreenComponent } from './layout/avantor-splash-screen/avantor-splash-screen';
import { AvantorLayoutComponent } from './layout/avantor-layout/avantor-layout';
import { AccessDeniedComponent } from './layout/access-denied/access-denied';
import { AvantorNotFoundComponent } from '@shared/components/avantor-not-found/avantor-not-found';

export const routes: Routes = [
  // ─── Splash / entry ───────────────────────────────────────────────────────
  {
    path: '',
    pathMatch: 'full',
    canActivate: [LoadGuard],
    component: AvantorSplashScreenComponent,
  },
  {
    path: SITE_LINK.USER_MAINTENANCE.ACCESS_DENIED,
    pathMatch: 'full',
    component: AccessDeniedComponent,
  },

  // ─── Auth (org & stockroom selection) ─────────────────────────────────────
  {
    path: 'auth',
    canActivate: [AuthGuard],
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: SITE_LINK.ORG_AND_STOCKROOM.BASE,
      },
      {
        path: SITE_LINK.ORG_AND_STOCKROOM.BASE,
        loadChildren: () =>
          import('./features/org-and-stockroom/routes').then(m => m.ROUTES),
      },
    ],
  },

  // ─── Main app shell ───────────────────────────────────────────────────────
  {
    path: 'app',
    canActivate: [AppGuard],
    canActivateChild: [AppGuard],
    component: AvantorLayoutComponent,
    children: [

      // Home / Dashboard
      {
        path: '',
        pathMatch: 'full',
        loadChildren: () =>
          import('./features/home/routes').then(m => m.ROUTES),
      },

      // ── Activities ────────────────────────────────────────────────────────
      {
        path: SITE_LINK.ACTIVITY_CREATE_BARCODES.BASE,
        canActivate: [PrivilegeGuard],
        data: { breadcrumb: BreadcrumbConstant.ACTIVITY_CREATE_BARCODES },
        loadChildren: () =>
          import('./features/create-barcodes/routes').then(m => m.ROUTES),
      },
      {
        path: SITE_LINK.ACTIVITY_STOCK_CORRECTION.BASE,
        canActivate: [PrivilegeGuard],
        data: { breadcrumb: BreadcrumbConstant.ACTIVITY_STOCK_CORRECTION_RECEIVE_STOCK },
        loadChildren: () =>
          import('./features/stock-correction/routes').then(m => m.ROUTES),
      },
      {
        path: SITE_LINK.ACTIVITY_STOCK_TRANSFER.BASE,
        canActivate: [PrivilegeGuard],
        data: { breadcrumb: BreadcrumbConstant.ACTIVITY_STOCK_TRANSFER },
        loadChildren: () =>
          import('./features/stock-transfer/routes').then(m => m.ROUTES),
      },
      {
        path: SITE_LINK.ACTIVITY_CONSUME_STOCK.BASE,
        canActivate: [PrivilegeGuard],
        data: {
          breadcrumb: BreadcrumbConstant.ACTIVITY_CONSUME_STOCK,
          preload: true,
        },
        loadChildren: () =>
          import('./features/consume-stock/routes').then(m => m.ROUTES),
      },
      {
        path: SITE_LINK.ACTIVITY_RECEIVE_STOCK.BASE,
        canActivate: [PrivilegeGuard],
        data: { breadcrumb: BreadcrumbConstant.ACTIVITY_RECEIVE_STOCK },
        loadChildren: () =>
          import('./features/receive-stock/routes').then(m => m.ROUTES),
      },
      {
        path: SITE_LINK.ACTIVITY_RETURN_STOCK.BASE,
        canActivate: [PrivilegeGuard],
        data: { breadcrumb: BreadcrumbConstant.ACTIVITY_RETURN_STOCK },
        loadChildren: () =>
          import('./features/return-stock/routes').then(m => m.ROUTES),
      },

      // ── Replenish ─────────────────────────────────────────────────────────
      {
        path: SITE_LINK.ACTIVITY_REPLENISH_STOCK.BASE,
        canActivate: [PrivilegeGuard],
        data: { breadcrumb: BreadcrumbConstant.ACTIVITY_REPLENISH_STOCK },
        loadChildren: () =>
          import('./features/replenish/consumption/routes').then(m => m.ROUTES),
      },
      {
        path: SITE_LINK.ACTIVITY_REPLENISH_STOCK.PUT_AWAY,
        canActivate: [PrivilegeGuard],
        data: { breadcrumb: BreadcrumbConstant.ACTIVITY_REPLENISH_STOCK },
        loadChildren: () =>
          import('./features/replenish/put-away/routes').then(m => m.ROUTES),
      },
      {
        path: SITE_LINK.ACTIVITY_REPLENISH_STOCK.ON_HAND_MODE,
        canActivate: [PrivilegeGuard],
        data: { breadcrumb: BreadcrumbConstant.ACTIVITY_REPLENISH_STOCK_ONHAND },
        loadChildren: () =>
          import('./features/replenish/stock-onhand/routes').then(m => m.ROUTES),
      },
      {
        path: SITE_LINK.ACTIVITY_REPLENISH_STOCK.ISSUANCE_MODE,
        canActivate: [PrivilegeGuard],
        data: { breadcrumb: BreadcrumbConstant.ACTIVITY_REPLENISH_STOCK_ISSUENCE },
        loadChildren: () =>
          import('./features/replenish/issuance/routes').then(m => m.ROUTES),
      },

      // ── Scanner Replenish ─────────────────────────────────────────────────
      {
        path: SITE_LINK.SCANNER_REPLENISH.ON_HAND_MODE,
        canActivate: [PrivilegeGuard],
        data: { breadcrumb: BreadcrumbConstant.ACTIVITY_SCANNER_REPLENISH_ONHAND },
        loadChildren: () =>
          import('./features/replenish/scanner-replenish/on-hand/routes').then(m => m.ROUTES),
      },
      {
        path: SITE_LINK.SCANNER_REPLENISH.PUT_AWAY_MODE,
        canActivate: [PrivilegeGuard],
        data: { breadcrumb: BreadcrumbConstant.ACTIVITY_SCANNER_REPLENISH_PUTAWAY },
        loadChildren: () =>
          import('./features/replenish/scanner-replenish/put-away/routes').then(m => m.ROUTES),
      },

      // ── Approval ──────────────────────────────────────────────────────────
      {
        path: SITE_LINK.APPROVAL.BASE,
        canActivate: [PrivilegeGuard],
        data: { breadcrumb: BreadcrumbConstant.ACTIVITY_APPROVAL_CONSUME_REQUEST },
        loadChildren: () =>
          import('./features/approval/routes').then(m => m.ROUTES),
      },
      // ── Overview ──────────────────────────────────────────────────────────
      {
        path: SITE_LINK.ACTIVITY_LOG.BASE,
        data: { breadcrumb: BreadcrumbConstant.OVERVIEW_ARTICLE_ACTIVITY_LOG },
        loadChildren: () =>
          import('./features/activity-log/routes').then(m => m.ROUTES),
      },
      {
        path: SITE_LINK.ORDERS.BASE,
        canActivate: [PrivilegeGuard],
        data: { breadcrumb: BreadcrumbConstant.OVERVIEW_ORDERS },
        loadChildren: () =>
          import('./features/orders/routes').then(m => m.ROUTES),
      },
      {
        path: SITE_LINK.PRODUCTS.BASE,
        canActivate: [PrivilegeGuard],
        data: { breadcrumb: BreadcrumbConstant.OVERVIEW_PRODUCTS },
        loadChildren: () =>
          import('./features/products/routes').then(m => m.ROUTES),
      },
      {
        path: SITE_LINK.REQUESTS.BASE,
        canActivate: [PrivilegeGuard],
        data: { breadcrumb: BreadcrumbConstant.OVERVIEW_REQUESTS },
        loadChildren: () =>
          import('./features/requests/routes').then(m => m.ROUTES),
      },
      {
        path: SITE_LINK.SEARCH_RESULT.BASE,
        canActivate: [PrivilegeGuard],
        data: { breadcrumb: BreadcrumbConstant.SEARCH_RESULTS },
        loadChildren: () =>
          import('./features/home/pages/search-results/routes').then(m => m.ROUTES),
      },
      {
        path: SITE_LINK.TRACK_ORDERS.BASE,
        canActivate: [PrivilegeGuard],
        data: { breadcrumb: BreadcrumbConstant.TRACK_ORDERS },
        loadChildren: () =>
          import('./features/track-orders/routes').then(m => m.ROUTES),
      },

      // ── Administration ────────────────────────────────────────────────────
      {
        path: SITE_LINK.PRODUCT_MAINTENANCE.BASE,
        canActivate: [PrivilegeGuard],
        data: { breadcrumb: BreadcrumbConstant.ADMINISTRATION_PRODUCT_MAINTENANCE },
        loadChildren: () =>
          import('./features/product-maintenance/routes').then(m => m.ROUTES),
      },
      {
        path: SITE_LINK.LOCATION_MAINTENANCE.BASE,
        canActivate: [PrivilegeGuard],
        data: { breadcrumb: BreadcrumbConstant.ADMINISTRATION_LOCATION_MAINTENANCE },
        loadChildren: () =>
          import('./features/location-maintenance/routes').then(m => m.ROUTES),
      },
      {
        path: SITE_LINK.USER_MAINTENANCE.BASE,
        canActivate: [PrivilegeGuard],
        data: { breadcrumb: BreadcrumbConstant.ADMINISTRATION_USER_MAINTENANCE },
        loadChildren: () =>
          import('./features/user-maintenance/routes').then(m => m.ROUTES),
      },
      {
        path: SITE_LINK.ACCOUNT_SETTINGS.BASE,
        canActivate: [PrivilegeGuard],
        data: { breadcrumb: BreadcrumbConstant.ACCOUNT_SETTINGS },
        loadChildren: () =>
          import('./features/account-settings/routes').then(m => m.ROUTES),
      },
      {
        path: SITE_LINK.MANAGE_STOCKROOM.BASE,
        canActivate: [PrivilegeGuard],
        data: { breadcrumb: BreadcrumbConstant.MANAGE_STOCKROOM },
        loadChildren: () =>
          import('./features/manage-stockroom/routes').then(m => m.ROUTES),
      },
      {
        path: SITE_LINK.EXPORT_IMPORT_STOCKROOM.BASE,
        canActivate: [PrivilegeGuard],
        data: { breadcrumb: BreadcrumbConstant.MANAGE_STOCKROOM },
        loadChildren: () =>
          import('./features/export-import-stockroom/routes').then(m => m.ROUTES),
      },
      {
        path: SITE_LINK.DOWNLOAD_CENTER.BASE,
        canActivate: [PrivilegeGuard],
        data: { breadcrumb: BreadcrumbConstant.DOWNLOAD_CENTER },
        loadChildren: () =>
          import('./features/download-center/routes').then(m => m.ROUTES),
      },
      {
        path: SITE_LINK.REPORTING.BASE,
        canActivate: [PrivilegeGuard],
        data: { breadcrumb: BreadcrumbConstant.REPORTING },
        loadChildren: () =>
          import('./features/reporting/routes').then(m => m.ROUTES),
      },
      {
        path: SITE_LINK.COMMUNICATIONS.BASE,
        canActivate: [PrivilegeGuard],
        data: { breadcrumb: BreadcrumbConstant.COMMUNICATIONS },
        loadChildren: () =>
          import('./features/communications/routes').then(m => m.ROUTES),
      },
      {
        path: SITE_LINK.REASON_CODE_MAINTENANCE.BASE,
        canActivate: [PrivilegeGuard],
        data: { breadcrumb: BreadcrumbConstant.ADMINISTRATION_REASON_CODE_MAINTENANCE },
        loadChildren: () =>
          import('./features/reason-code-maintenance/routes').then(m => m.ROUTES),
      },
      {
        path: SITE_LINK.COST_CENTER_MAINTENANCE.BASE,
        canActivate: [PrivilegeGuard],
        data: { breadcrumb: BreadcrumbConstant.ADMINISTRATION_COST_CENTER_MAINTENANCE },
        loadChildren: () =>
          import('./features/cost-center-maintenance/routes').then(m => m.ROUTES),
      },
      {
        path: SITE_LINK.DEPARTMENT_MAINTENANCE.BASE,
        canActivate: [PrivilegeGuard],
        data: { breadcrumb: BreadcrumbConstant.ADMINISTRATION_DEPARTMENT_MAINTENANCE },
        loadChildren: () =>
          import('./features/department-maintenance/routes').then(m => m.ROUTES),
      },
      {
        path: SITE_LINK.VENDOR_MAINTENANCE.BASE,
        canActivate: [PrivilegeGuard],
        data: { breadcrumb: BreadcrumbConstant.ADMINISTRATION_VENDOR_MAINTENANCE },
        loadChildren: () =>
          import('./features/vendor-maintenance/routes').then(m => m.ROUTES),
      },
      {
        path: SITE_LINK.INTEGRATION_MAINTENANCE.BASE,
        canActivate: [PrivilegeGuard],
        data: { breadcrumb: BreadcrumbConstant.ADMINISTRATION_INTEGRATION_MAINTENANCE },
        loadChildren: () =>
          import('./features/integration-maintenance/routes').then(m => m.ROUTES),
      },
      {
        path: SITE_LINK.REFERENCE_CODE_FIELD_UOM.BASE,
        canActivate: [PrivilegeGuard],
        data: { breadcrumb: BreadcrumbConstant.ADMINISTRATION_REFERENCE_CODES },
        loadChildren: () =>
          import('./features/reference-code-maintenance/routes').then(m => m.ROUTES),
      },
      {
        path: SITE_LINK.MESSAGE_CENTER.BASE,
        canActivate: [PrivilegeGuard],
        data: { breadcrumb: BreadcrumbConstant.ADMINISTRATION_MESSAGE_CENTER },
        loadChildren: () =>
          import('./features/message-center/routes').then(m => m.ROUTES),
      },

      // ── Admin Console ─────────────────────────────────────────────────────
      {
        path: SITE_LINK.ADMIN_CONSOLE.BASE,
        canActivate: [PrivilegeGuard],
        data: { breadcrumb: BreadcrumbConstant.ORGANIZATION_MAINTANANCE },
        loadChildren: () =>
          import('./features/admin-console/organization/routes').then(m => m.ROUTES),
      },
      {
        path: SITE_LINK.ADMIN_ORDER_TRACKING.BASE,
        canActivate: [PrivilegeGuard],
        data: { breadcrumb: BreadcrumbConstant.TRACK_ORDERS },
        loadChildren: () =>
          import('./features/admin-console/order-tracking/routes').then(m => m.ROUTES),
      },
      {
        path: SITE_LINK.ADMIN_CENTER_ACTIVITY_LOG.BASE,
        canActivate: [PrivilegeGuard],
        data: { breadcrumb: BreadcrumbConstant.ADMIN_CENTER_ACTIVITY_LOG },
        loadChildren: () =>
          import('./features/admin-console/admin-center-activity-log/routes').then(m => m.ROUTES),
      },
      {
        path: SITE_LINK.ADMIN_ROLE_MAINTENANCE.BASE,
        canActivate: [PrivilegeGuard],
        data: { breadcrumb: BreadcrumbConstant.ADMINISTRATION_USER_MAINTENANCE_ROLES },
        loadChildren: () =>
          import('./features/admin-console/role-maintenance/routes').then(m => m.ROUTES),
      },
      {
        path: SITE_LINK.ADMIN_CONSOLE_NEWS_CENTER.BASE,
        canActivate: [PrivilegeGuard],
        data: { breadcrumb: BreadcrumbConstant.ADMINISTRATION_NEWS_CENTER },
        loadChildren: () =>
          import('./features/admin-console/news-center/routes').then(m => m.ROUTES),
      },
      {
        path: SITE_LINK.ADMIN_DOWNLOAD_CENTER.BASE,
        canActivate: [PrivilegeGuard],
        data: { breadcrumb: BreadcrumbConstant.ADMIN_DOWNLOAD_CENTER },
        loadChildren: () =>
          import('./features/admin-console/download-center/routes').then(m => m.ROUTES),
      },
      {
        path: SITE_LINK.ADMIN_MANAGE_BARCODING.BASE,
        canActivate: [PrivilegeGuard],
        data: { breadcrumb: BreadcrumbConstant.ADMINISTRATION_BARCODE_MAINTENANCE },
        loadChildren: () =>
          import('./features/admin-console/barcoding/routes').then(m => m.ROUTES),
      },
      {
        path: SITE_LINK.ADMIN_GLOBAL_METRICS.BASE,
        canActivate: [PrivilegeGuard],
        data: { breadcrumb: BreadcrumbConstant.ADMINISTRATION_GLOBAL_METRICS },
        loadChildren: () =>
          import('./features/admin-console/global-metrics/routes').then(m => m.ROUTES),
      },
      {
        path: SITE_LINK.ADMIN_SUPER_USERS_COUNTRY_VIEWS.BASE,
        canActivate: [PrivilegeGuard],
        data: { breadcrumb: BreadcrumbConstant.ORGANIZATION_SUPER_USERS_COUNTRY_ADMIN_VIEWS },
        loadChildren: () =>
          import('./features/admin-console/super-users-country-admins/routes').then(m => m.ROUTES),
      },

      // ── Language modules (new features from V13) ──────────────────────────
      {
        path: SITE_LINK.LANGUAGE_TRANSLATION.BASE,
        canActivate: [PrivilegeGuard],
        data: { breadcrumb: BreadcrumbConstant.ADMINISTRATION_LANGUAGE_TRANSLATION },
        loadChildren: () =>
          import('./features/admin-console/language-translation/routes').then(m => m.ROUTES),
      },
      {
        path: SITE_LINK.SCHEDULING.BASE,
        canActivate: [PrivilegeGuard],
        data: { breadcrumb: BreadcrumbConstant.ADMINISTRATION_SCHEDULING },
        loadChildren: () =>
          import('./features/admin-console/scheduling/routes').then(m => m.ROUTES),
      },
      {
        path: SITE_LINK.ADMIN_CONSOLE_LANGUAGE_KEY.BASE,
        canActivate: [PrivilegeGuard],
        data: { breadcrumb: BreadcrumbConstant.ADMINISTRATION_LANGUAGE_KEYS },
        loadChildren: () =>
          import('./features/language-keys/routes').then(m => m.ROUTES),
      },
      {
        path: SITE_LINK.ADMIN_CONSOLE_SUPPORT_TICKET_REPORTS.BASE,
        canActivate: [PrivilegeGuard],
        data: { breadcrumb: BreadcrumbConstant.ADMINISTRATION_SUPPORT_TICKET_REPORTS },
        loadChildren: () =>
          import('./features/support-ticket-reports/routes').then(m => m.ROUTES),
      },

      // ── Misc ──────────────────────────────────────────────────────────────
      {
        path: SITE_LINK.HELP.BASE,
        data: { breadcrumb: BreadcrumbConstant.DOWNLOAD_CENTER },
        loadChildren: () =>
          import('./features/help/routes').then(m => m.ROUTES),
      },
      {
        path: SITE_LINK.FREQUENTLY_ASKED_QUESTIONS.BASE,
        data: { breadcrumb: BreadcrumbConstant.SUPPORT_FAQS },
        loadChildren: () =>
          import('./features/frequently-asked-questions/routes').then(m => m.ROUTES),
      },
      {
        path: SITE_LINK.API_SERVICES.BASE,
        data: { breadcrumb: BreadcrumbConstant.API_SERVICES },
        loadChildren: () =>
          import('./features/api-services/routes').then(m => m.ROUTES),
      },
    ],
  },

  // ─── 404 ──────────────────────────────────────────────────────────────────
  {
    path: 'not-found',
    component: AvantorNotFoundComponent,
  },
  {
    path: '**',
    pathMatch: 'full',
    redirectTo: 'auth',
  },
];
